/* banner-ultimate-mto.js */
(() => {
  if (customElements.get('banner-ultimate-mto')) return;

  class BannerUltimateMto extends HTMLElement {
    constructor() {
      super();
      this._abort = new AbortController();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const s = this._abort.signal;

      this.track = this.querySelector('[data-bn-track]');
      if (!this.track) return;

      this.total = parseInt(this.dataset.total, 10) || 0;
      this.isSingle = this.dataset.single === 'true' || this.total < 2;
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(1000, parseInt(this.dataset.interval, 10) || 5000);
      this.pauseHover = this.dataset.pauseHover === 'true';

      // UI Elements
      this.dots = Array.from(this.querySelectorAll('.bn-dot'));
      this.prevBtn = this.querySelector('[data-bn-prev]');
      this.nextBtn = this.querySelector('[data-bn-next]');

      // State
      this.index = 0;
      this.isAnimating = false;
      this._metrics = { gap: 0, slideW: 0, step: 0, base: 0 };
      this._autoplayTimer = null;
      this._scrollTimer = null;

      // Init Loop if needed
      if (!this.isSingle) this._buildLoop();

      // Events
      this._bindEvents(s);

      // Observers
      this._resizeObserver = new ResizeObserver(() => this._onResize());
      this._resizeObserver.observe(this.track);

      this._intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          this._isInView = e.isIntersecting;
          if (this._isInView) {
            this._armAutoplay();
            this._playActiveVideo();
          } else {
            this._stopAutoplay();
            this._pauseAllVideos();
          }
        });
      }, { threshold: 0.1 });
      this._intersectionObserver.observe(this);

      // Initial Setup
      requestAnimationFrame(() => {
        this._measure();
        if (!this.isSingle) {
          this.track.classList.add('no-snap');
          this._jump(0);
          requestAnimationFrame(() => this.track.classList.remove('no-snap'));
        }
        this.setAttribute('data-ready', 'true');
        this._updateUI();
        this._playActiveVideo();
        this._armAutoplay();
      });
    }

    disconnectedCallback() {
      this._stopAutoplay();
      clearTimeout(this._scrollTimer);
      this._abort.abort();
      this._resizeObserver?.disconnect();
      this._intersectionObserver?.disconnect();
    }

    _measure() {
      const slide = this.track.querySelector('.bn-slide:not(.is-clone)');
      if (!slide) return;

      const cs = getComputedStyle(this.track);
      const gap = parseFloat(cs.columnGap || cs.gap || 0);
      const w = slide.offsetWidth;
      const tw = this.track.clientWidth;

      // Edge Padding logic to center slides
      const centerOff = (tw - w) / 2;
      const edgePad = Math.max(0, centerOff - gap);
      this.style.setProperty('--bn-edge-pad', `${edgePad}px`);

      this._metrics = {
        gap,
        slideW: w,
        step: w + gap,
        base: Math.max(0, (edgePad + gap) - centerOff)
      };
    }

    _buildLoop() {
      const slides = Array.from(this.track.children);
      const first = slides[0];
      const last = slides[slides.length - 1];

      const cloneFirst = first.cloneNode(true);
      const cloneLast = last.cloneNode(true);

      this._sanitize(cloneFirst);
      this._sanitize(cloneLast);

      const edgeS = document.createElement('div'); edgeS.className = 'bn-edge';
      const edgeE = document.createElement('div'); edgeE.className = 'bn-edge';

      this.track.prepend(cloneLast);
      this.track.prepend(edgeS);
      this.track.append(cloneFirst);
      this.track.append(edgeE);

      this.allSlides = Array.from(this.querySelectorAll('.bn-slide'));
    }

    _sanitize(el) {
      el.classList.add('is-clone');
      el.removeAttribute('id');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      el.querySelectorAll('video').forEach(v => { v.pause(); v.removeAttribute('autoplay'); });
      el.querySelectorAll('img').forEach(i => { i.loading = 'lazy'; i.removeAttribute('fetchpriority'); });
    }

    _bindEvents(signal) {
      this.prevBtn?.addEventListener('click', () => this._shift(-1), { signal });
      this.nextBtn?.addEventListener('click', () => this._shift(1), { signal });

      this.dots.forEach(d => {
        d.addEventListener('click', () => {
          const i = parseInt(d.dataset.idx, 10);
          if (!isNaN(i)) this._scrollTo(i);
        }, { signal });
      });

      // Drag Pause
      this.track.addEventListener('pointerdown', () => this._stopAutoplay(), { signal, passive: true });
      this.track.addEventListener('pointerup', () => this._armAutoplay(), { signal, passive: true });

      // Scroll Sync
      this.track.addEventListener('scroll', () => {
        if (this.isAnimating) return;
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(() => this._onScrollStop(), 60);
      }, { signal, passive: true });

      if (this.pauseHover) {
        this.addEventListener('mouseenter', () => this._stopAutoplay(), { signal });
        this.addEventListener('mouseleave', () => this._armAutoplay(), { signal });
      }

      // Shopify Editor
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const slide = e.target.closest('.bn-slide');
          if (slide && this.contains(slide) && !slide.classList.contains('is-clone')) {
            this._scrollTo(parseInt(slide.dataset.idx));
          }
        }, { signal });
      }
    }

    _shift(dir) {
      if (this.isAnimating) return;
      this._stopAutoplay();

      let next = this.index + dir;

      if (this.isSingle) {
        if (next < 0) next = 0;
        if (next >= this.total) next = this.total - 1;
        this._scrollTo(next);
        return;
      }

      // Infinite Logic
      if (next < 0) this._loop(-1);
      else if (next >= this.total) this._loop(1);
      else this._scrollTo(next);
    }

    _scrollTo(idx, smooth = true) {
      this.index = idx;
      this._updateUI();

      const pos = this._metrics.base + ((this.isSingle ? idx : idx + 1) * this._metrics.step);

      this.isAnimating = true;
      this.track.scrollTo({ left: pos, behavior: smooth ? 'smooth' : 'auto' });

      // Wait for scroll to finish
      this._waitScroll().then(() => {
        this.isAnimating = false;
        this._playActiveVideo();
        this._armAutoplay();
      });
    }

    _jump(idx) {
      const pos = this._metrics.base + ((this.isSingle ? idx : idx + 1) * this._metrics.step);
      this.track.scrollTo({ left: pos });
    }

    _loop(dir) {
      this.isAnimating = true;
      this.track.classList.add('no-snap');

      // 1. Move to clone
      const cloneIdx = dir === 1 ? this.total + 1 : 0;
      const pos = this._metrics.base + (cloneIdx * this._metrics.step);
      this.track.scrollTo({ left: pos, behavior: 'smooth' });

      // 2. Teleport back
      this._waitScroll().then(() => {
        this.index = dir === 1 ? 0 : this.total - 1;
        this._updateUI();
        this._jump(this.index);

        requestAnimationFrame(() => {
          this.track.classList.remove('no-snap');
          this.isAnimating = false;
          this._playActiveVideo();
          this._armAutoplay();
        });
      });
    }

    _waitScroll() {
      return new Promise(r => {
        let last = -1, count = 0, ticks = 0;
        const check = () => {
          if (!this.isConnected || ++ticks > 120) { r(); return; }
          const curr = this.track.scrollLeft;
          if (Math.abs(curr - last) < 2) count++; else count = 0;
          last = curr;
          if (count > 5) r(); else requestAnimationFrame(check);
        };
        check();
      });
    }

    _onScrollStop() {
      if (!this._metrics.step) return;
      const scroll = this.track.scrollLeft;
      const raw = (scroll - this._metrics.base) / this._metrics.step;
      const idx = Math.round(raw) - (this.isSingle ? 0 : 1);

      if (idx >= 0 && idx < this.total && idx !== this.index) {
        this.index = idx;
        this._updateUI();
        this._playActiveVideo();
      } else if (!this.isSingle && (idx < 0 || idx >= this.total)) {
        // Correct snap drift if user swiped to clone manually
        const fixed = idx < 0 ? this.total - 1 : 0;
        this.index = fixed;
        this.track.classList.add('no-snap');
        this._jump(fixed);
        requestAnimationFrame(() => this.track.classList.remove('no-snap'));
        this._updateUI();
      }

      // FIX: Re-arm autoplay after any manual scroll/swipe/trackpad interaction
      this._armAutoplay();
    }

    /**
     * Updates dots, arrows, and active slide class.
     */
    _updateUI() {
      // Update dots
      this.dots.forEach((d, i) => {
        const active = i === this.index;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-current', active);
      });

      // Update active slide class (skip clones and edge spacers)
      const realSlides = this.track.querySelectorAll('.bn-slide:not(.is-clone)');
      realSlides.forEach((slide) => {
        const slideIdx = parseInt(slide.dataset.idx, 10);
        slide.classList.toggle('is-active', slideIdx === this.index);
      });
    }

    _onResize() {
      this._measure();
      if (!this.isSingle) this._jump(this.index);
    }

    /* Autoplay */
    _armAutoplay() {
      this._stopAutoplay();
      if (!this.autoplay || this.isSingle || !this._isInView) return;
      this._autoplayTimer = setTimeout(() => this._shift(1), this.interval);
    }
    _stopAutoplay() { clearTimeout(this._autoplayTimer); }

    /* Video Management */
    _pauseAllVideos() {
      this.querySelectorAll('video').forEach(v => v.pause());
    }

    _playActiveVideo() {
      const childIdx = this.isSingle ? this.index : this.index + 2; // +2 = edge spacer + clone
      const activeSlide = this.track.children[childIdx];
      if (!activeSlide) return;

      this._pauseAllVideos();

      const isMobile = window.innerWidth < 750;

      // Try platform-specific wrapper first (when both desktop + mobile videos exist)
      let vid = activeSlide.querySelector(
        isMobile ? '.bn-video-mobile video' : '.bn-video-desktop video'
      );

      // FIX: Fallback to any direct video in the slide (when only one video is uploaded)
      if (!vid) {
        vid = activeSlide.querySelector('video.bn-video');
      }

      if (vid) {
        vid.muted = true;
        vid.play().catch(() => {});
      }
    }
  }
  customElements.define('banner-ultimate-mto', BannerUltimateMto);
})();